import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  useRecoilState,
  useRecoilValue,
  useSetRecoilState,
} from 'recoil';
import {
  impersonateModalAtom,
  impersonatorAtom,
  notificationsAtom,
  stateAtom,
  tokenAtom,
  userAtom,
} from '../atoms/Atoms';
import bookingApi from '../api/bookingApi';
import envConfig from '../envConfig';

const sk = envConfig.StorageKey;

// Centralizes the "act as another user" flow available to SuperAdmins. Starting
// an impersonation swaps the three persisted auth values (token/user/state) for
// the target user's, while keeping a snapshot of the SuperAdmin's own session so
// it can be restored later with a single click — no re-login required.
function useImpersonation() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const setUser = useSetRecoilState(userAtom);
  const setToken = useSetRecoilState(tokenAtom);
  const setState = useSetRecoilState(stateAtom);
  const user = useRecoilValue(userAtom);
  const [impersonator, setImpersonator] = useRecoilState(impersonatorAtom);
  const setModalOpen = useSetRecoilState(impersonateModalAtom);
  const setNotifications = useSetRecoilState(notificationsAtom);

  const isSuperAdmin = !!(user && user.roles && user.roles.includes('SuperAdmin'));
  const isImpersonating = !!impersonator;

  const notifyError = useCallback(
    (description) => {
      setNotifications((prev) => [
        ...prev,
        {
          title: `${t('oops')}`,
          description,
          error: true,
        },
      ]);
    },
    [setNotifications, t]
  );

  const startImpersonation = useCallback(
    async (userId) => {
      // Snapshot the current (SuperAdmin) session before we overwrite it.
      const snapshot = {
        token: window.localStorage.getItem(`${sk}-token`),
        user: window.localStorage.getItem(`${sk}-user`),
        state: window.localStorage.getItem(`${sk}-state`),
      };

      const response = await bookingApi.post('auth/impersonate', { userId });
      const body = response.data;
      if (body.error || !body.data || !body.data.accessToken) {
        notifyError(body.message || body.error || `${t('login_error')}`);
        return false;
      }

      const result = body.data;
      // Commit the snapshot, then swap in the impersonated session.
      window.localStorage.setItem(`${sk}-impersonator`, JSON.stringify(snapshot));
      window.localStorage.setItem(`${sk}-state`, JSON.stringify(result.state));
      window.localStorage.setItem(`${sk}-user`, JSON.stringify(result.state.profile));
      window.localStorage.setItem(`${sk}-token`, JSON.stringify(result.accessToken));

      setImpersonator(snapshot);
      setState(result.state);
      setUser(result.state.profile);
      setToken(result.accessToken);

      setModalOpen(false);
      navigate('/');
      return true;
    },
    [navigate, notifyError, setImpersonator, setModalOpen, setState, setToken, setUser, t]
  );

  const stopImpersonation = useCallback(() => {
    const raw = window.localStorage.getItem(`${sk}-impersonator`);
    let snapshot = impersonator;
    if (raw) {
      try {
        snapshot = JSON.parse(raw);
      } catch (e) {
        snapshot = impersonator;
      }
    }

    if (snapshot && snapshot.token) {
      window.localStorage.setItem(`${sk}-token`, snapshot.token);
    }
    if (snapshot && snapshot.user) {
      window.localStorage.setItem(`${sk}-user`, snapshot.user);
    }
    if (snapshot && snapshot.state) {
      window.localStorage.setItem(`${sk}-state`, snapshot.state);
    }
    window.localStorage.removeItem(`${sk}-impersonator`);

    setImpersonator(null);
    setToken(snapshot && snapshot.token ? JSON.parse(snapshot.token) : null);
    setUser(snapshot && snapshot.user ? JSON.parse(snapshot.user) : null);
    setState(snapshot && snapshot.state ? JSON.parse(snapshot.state) : null);

    navigate('/');
  }, [impersonator, navigate, setImpersonator, setState, setToken, setUser]);

  return {
    isSuperAdmin,
    isImpersonating,
    impersonator,
    startImpersonation,
    stopImpersonation,
    openModal: () => setModalOpen(true),
  };
}

export default useImpersonation;
